import React, { useState } from "react";

const ChatBar = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false); // New loading state

  const predefinedContext = `
  Hi, My Name is Sameed Hayat, I'm a results-driven AI and Machine Learning Specialist with over 6 years of experience in developing cutting-edge solutions in NLP, Generative AI, and data analytics. I am currently a Senior Researcher at HLRS, where I focus on projects such as log anomaly detection and patent trend analysis. My expertise includes leveraging state-of-the-art techniques like large language models (LLMs), topic modeling, anomaly detection algorithms (Isolation Forest, LSTM, Autoencoders), and advanced visualization tools like Dash.
  
  During my professional journey, I have contributed to various industries, including cloud computing, telecom, and research. At SAP, I developed a semi-supervised model for de-identifying Personally Identifiable Information (PII), achieving 95% accuracy using Bi-LSTM and Temporal CNNs. My work at Fraunhofer involved ETL operations, data visualization, and GUI development for solar power plant simulations. At Huawei, I honed my skills in SQL, Python, and data analysis, automating workflows and generating business insights.
  
  ### Key Expertise:
  - Natural Language Processing (NLP) and Large Language Models (LLMs)
  - Generative AI for chatbots and innovative applications
  - Machine Learning and Deep Learning
  - Topic Modeling, Anomaly Detection, and Clustering
  - Data Visualization using Dash and Plotly
  - Cloud Computing and Containerization (Docker)
  
  ### Technologies & Tools:
  Python, PyTorch, TensorFlow, NLTK, SpaCy, Plotly, Dash, SQL, Docker, GitHub
  
  ### Hobbies & Interests:
  In my free time, I enjoy playing chess, watching thriller series, reading books, and listening to podcasts. These activities help me unwind and keep me curious about diverse subjects. I also enjoy exploring new trends in AI and engaging in intellectual challenges like strategic games.
  
  ### Recruiter-Friendly Notes:
  - I am open to roles such as Senior ML Engineer, AI Research Scientist, or Generative AI Specialist.
  - My ideal work environment encourages collaboration, innovation, and a focus on cutting-edge technology.
  - I'm passionate about using AI to solve real-world challenges and would love to contribute to projects involving NLP, anomaly detection, or LLM-powered systems.
  You can contact me via the following means
  Linkedin: https://www.linkedin.com/in/sameedhayat/
  Email: sameedhayt@gmail.com
  If you have any questions about my experience, skills, or interests, feel free to ask!
  `;

  // Example questions for suggestions
  const exampleQuestions = [
    "What are your expertises?",
    "What type of job are you looking for?",
    "How can I contact you?",
  ];

  const handleChatToggle = () => {
    setIsChatOpen(!isChatOpen);
  };

  const handleAsk = async () => {
    if (!question.trim()) {
      alert("Please enter a question!");
      return;
    }

    setLoading(true); // Show loading bar
    setResponse(""); // Clear previous response

    try {
      const apiResponse = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${process.env.REACT_APP_OPENROUTER_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: "mistralai/mistral-7b-instruct:free",
          messages: [
            { role: "system", content: "You are an assistant that answers questions based on the provided context. Keep the answers short and precise only two to three sentences. Add emojies also return the text in htmls format" },
            { role: "assistant", content: predefinedContext },
            { role: "user", content: question },
          ],
        }),
      });

      const data = await apiResponse.json();
      setResponse(data.choices[0].message.content || "No response available.");
    } catch (error) {
      console.error("Error fetching LLM response:", error);
      setResponse("Oops! Something went wrong.");
    } finally {
      setLoading(false); // Hide loading bar
    }
  };

  const handleExampleClick = (example) => {
    setQuestion(example); // Populate the input field with the selected question
  };

  return (
    <div>
      {/* Floating Chat Icon */}
      <div
        onClick={handleChatToggle}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          width: "70px",
          height: "70px",
          backgroundColor: "#4CAF50",
          borderRadius: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          zIndex: 1000,
        }}
      >
        <span
          className="iconify"
          data-icon="bi:chat-right-dots-fill"
          data-inline="false"
          style={{ color: "white", fontSize: "30px" }}
        ></span>
      </div>

      {/* Chat Box */}
      {isChatOpen && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            width: "350px",
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderRadius: "10px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            zIndex: 1000,
            padding: "15px",
          }}
        >
          <div style={{ maxHeight: "250px", overflowY: "auto", marginBottom: "15px", fontSize: "16px" }}>
            {response && !loading && (
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f1f1f1",
                  borderRadius: "5px",
                  marginBottom: "15px",
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: response }} />
              </div>
            )}
            {loading && (
              <div
                style={{
                  width: "100%",
                  height: "20px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "5px",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: "50%",
                    height: "100%",
                    backgroundColor: "#4CAF50",
                    animation: "loading 1.5s infinite",
                    position: "absolute",
                  }}
                ></div>
                <style>
                  {`
                    @keyframes loading {
                      0% { left: -50%; }
                      50% { left: 0%; }
                      100% { left: 100%; }
                    }
                  `}
                </style>
              </div>
            )}
          </div>

          {/* Input and Send Button */}
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Type your question..."
            style={{
              width: "100%",
              height: "80px",
              padding: "12px",
              fontSize: "16px",
              border: "1px solid #ddd",
              borderRadius: "5px",
              resize: "none",
              marginBottom: "15px",
            }}
          />
          <button
            onClick={handleAsk}
            style={{
              width: "100%",
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              padding: "12px",
              fontSize: "16px",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Ask questions about me!
          </button>

          {/* Example Questions Below the Button */}
          <div style={{ marginTop: "15px" }}>
            <p style={{ fontWeight: "bold", marginBottom: "10px" }}>Example Questions:</p>
            {exampleQuestions.map((example, index) => (
              <button
                key={index}
                onClick={() => handleExampleClick(example)}
                style={{
                  display: "block",
                  backgroundColor: "#f1f1f1",
                  color: "#333",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  padding: "8px 12px",
                  margin: "5px 0",
                  cursor: "pointer",
                  fontSize: "14px",
                  textAlign: "left",
                  width: "100%",
                }}
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBar;
